import { useEffect, useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarDays,
  Inbox,
  LogOut,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Clock,
  Check,
  X,
  AlertCircle,
  LayoutDashboard,
  Building,
  Settings,
  Edit3,
  Sparkles,
  UserCog,
  Users,
  Receipt,
  Globe,
  Plus,
  Send,
  ClipboardList,
  BookOpen,
} from "lucide-react";

import { api, BRAND, formatApiErrorDetail } from "@/lib/api";
import DashboardPanel from "@/components/admin/Dashboard";
import ServicesManager from "@/components/admin/ServicesManager";
import LocationsManager from "@/components/admin/LocationsManager";
import ContentEditor from "@/components/admin/ContentEditor";
import AreasManager from "@/components/admin/AreasManager";
import AccountSettings from "@/components/admin/AccountSettings";
import ClientsManager from "@/components/admin/ClientsManager";
import IntakeFormsManager from "@/components/admin/IntakeFormsManager";
import AvailabilityInspector from "@/components/admin/AvailabilityInspector";
import FinanceWorkspace from "@/components/admin/FinanceWorkspace";
import BlogManager from "@/components/admin/BlogManager";
import AdminBookingModal from "@/components/admin/AdminBookingModal";
import WorkingHours from "@/components/admin/WorkingHours";
import BookingDetailModal, { bookingCellStyle } from "@/components/admin/BookingDetailModal";

const TOKEN_KEY = "ams_token";
const USER_KEY = "ams_user";

const WORKSPACES = [
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "clients", label: "Clients", icon: Users },
  { id: "intake", label: "Intake forms", icon: ClipboardList },
  { id: "finance", label: "Finance", icon: Receipt },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "website", label: "Website", icon: Globe },
  { id: "account", label: "Account", icon: UserCog },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });

  useEffect(() => {
    if (!token) return;
    api.get("/admin/me").catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
    });
  }, [token]);

  if (!token) {
    return (
      <Login
        onSuccess={(t, u) => {
          localStorage.setItem(TOKEN_KEY, t);
          localStorage.setItem(USER_KEY, JSON.stringify(u));
          setToken(t);
          setUser(u);
        }}
      />
    );
  }

  return (
    <Shell
      user={user}
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
      }}
    />
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      onSuccess(data.token, { email: data.email, name: data.name });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={BRAND.logoUrl} alt="" className="h-16 w-16 rounded-full mx-auto bg-ink p-0.5 mb-5" />
          <div className="text-xs uppercase tracking-[0.22em] text-navy mb-2">Admin area</div>
          <h1 className="font-serif text-3xl text-ink">Welcome back, Jesse.</h1>
        </div>
        <form onSubmit={submit} className="bg-cream-soft border border-sand/70 rounded-[2rem] p-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 block">Email</label>
            <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-cream border border-sand/80 rounded-2xl px-5 py-3.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 block">Password</label>
            <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-cream border border-sand/80 rounded-2xl px-5 py-3.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy" />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle size={16} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-7 py-3.5 text-sm font-medium hover:bg-navy-deep transition-all disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Sign in
          </button>
        </form>
        <a href="/" className="block text-center text-sm text-ink-soft hover:text-navy mt-6 transition-colors">← Back to site</a>
      </motion.div>
    </div>
  );
}

function Shell({ user, onLogout }) {
  const [workspace, setWorkspace] = useState(() => localStorage.getItem("ams_admin_workspace") || "calendar");
  useEffect(() => { localStorage.setItem("ams_admin_workspace", workspace); }, [workspace]);

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-cream-soft border-b border-sand/60 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="" className="h-10 w-10 rounded-full bg-ink/95 p-0.5" />
            <div className="leading-tight">
              <div className="font-serif text-lg text-ink">A Mindful Space · Admin</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Signed in as {user?.name || user?.email || "Jesse"}</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2 text-sm text-ink hover:border-navy hover:text-navy transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Workspace selector */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex gap-1 overflow-x-auto -mb-px">
            {WORKSPACES.map((w) => {
              const Icon = w.icon;
              const active = workspace === w.id;
              return (
                <button
                  key={w.id}
                  onClick={() => setWorkspace(w.id)}
                  data-testid={`workspace-${w.id}`}
                  className={`inline-flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                    active ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
                  }`}
                >
                  <Icon size={16} />
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <PendingNotesBanner onJump={(b) => {
          setWorkspace("calendar");
          // Wait for CalendarWorkspace to mount & register its listener before firing
          setTimeout(() => window.dispatchEvent(new CustomEvent("ams:open-booking", { detail: b })), 50);
        }} />
        {workspace === "calendar" && <CalendarWorkspace />}
        {workspace === "clients" && <ClientsManager />}
        {workspace === "intake" && <IntakeFormsManager />}
        {workspace === "finance" && <FinanceWorkspace />}
        {workspace === "blog" && <BlogManager />}
        {workspace === "website" && <WebsiteWorkspace />}
        {workspace === "account" && <AccountSettings user={user} />}
      </div>
    </div>
  );
}

// =================== CALENDAR WORKSPACE ===================
function CalendarWorkspace() {
  const [section, setSection] = useState("schedule"); // 'schedule' | 'hours'
  const [view, setView] = useState(() => localStorage.getItem("ams_calendar_view") || "list");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [autoOpenNotes, setAutoOpenNotes] = useState(false);

  useEffect(() => { localStorage.setItem("ams_calendar_view", view); }, [view]);
  // Legacy: some sessions had a location filter stored; clear it so nothing hides.
  useEffect(() => { localStorage.removeItem("ams_calendar_loc"); }, []);

  // Listen for cross-component "open this booking's notes" requests
  // (fired from the dashboard PendingNotesBanner).
  useEffect(() => {
    const handler = (e) => {
      const target = e.detail;
      if (!target?.id) return;
      api.get("/admin/bookings").then(({ data }) => {
        const found = data.find((b) => b.id === target.id);
        if (found) {
          setAutoOpenNotes(true);
          setSelected(found);
        }
      });
    };
    window.addEventListener("ams:open-booking", handler);
    return () => window.removeEventListener("ams:open-booking", handler);
  }, []);

  // Collapse all-locations block siblings into ONE display entry per group.
  const groupedBookings = (() => {
    const seenGroup = new Set();
    const out = [];
    for (const b of bookings) {
      if (b.kind === "block" && b.block_group_id) {
        // Collapse sibling all-locations blocks per OCCURRENCE (per date). Keep every
        // date in a recurring series — only sibling locations on the SAME date are
        // deduplicated. Composite key = block_group_id + date.
        const key = `${b.block_group_id}::${b.date}`;
        if (seenGroup.has(key)) continue;
        seenGroup.add(key);
        out.push({ ...b, _grouped: true, location_name: "All locations" });
      } else {
        out.push(b);
      }
    }
    return out;
  })();

  const filteredBookings = groupedBookings;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendReminder = async (b) => {
    if (!window.confirm(`Send reminder email to ${b.client_name} (${b.client_email})?`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/reminder`);
      toast.success(`Reminder sent to ${b.client_name.split(" ")[0]}`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reminder");
    }
  };

  return (
    <div data-testid="calendar-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Calendar</div>
          <h2 className="font-serif text-3xl text-ink">
            {section === "schedule" ? "Your sessions, at a glance." : "Set when you're available."}
          </h2>
          {section === "schedule" && (
            <p className="text-sm text-ink-soft mt-1">{bookings.length} booking{bookings.length === 1 ? "" : "s"} total.</p>
          )}
        </div>
        {section === "schedule" && (
          <div className="flex items-center gap-3">
            <ViewToggle view={view} onChange={setView} />
            <button
              onClick={() => setShowNew(true)}
              data-testid="new-booking-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
            >
              <Plus size={14} /> New booking
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-6 border-b border-sand/60">
        <button
          onClick={() => setSection("schedule")}
          data-testid="calendar-section-schedule"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
            section === "schedule" ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
          }`}
        >
          <CalendarDays size={14} /> Schedule
        </button>
        <button
          onClick={() => setSection("hours")}
          data-testid="calendar-section-hours"
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
            section === "hours" ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
          }`}
        >
          <Settings size={14} /> Working hours
        </button>
      </div>

      {section === "hours" ? (
        <WorkingHours />
      ) : (
        <>
          <WeeklyHoursSummary refreshKey={section} onEdit={() => setSection("hours")} />
          <AvailabilityInspector />
          {loading ? (
            <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>
          ) : (
            <>
              {view === "list" ? (
                <BookingsList bookings={filteredBookings} onUpdate={updateBookingStatus} onSelect={setSelected} onSendReminder={sendReminder} />
              ) : view === "week" ? (
                <WeekView bookings={filteredBookings} onSelect={setSelected} />
              ) : (
                <MonthView bookings={filteredBookings} onSelect={setSelected} />
              )}
            </>
          )}
        </>
      )}

      {showNew && (
        <AdminBookingModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); refresh(); }}
        />
      )}

      {selected && (
        <BookingDetailModal
          booking={selected}
          openNotes={autoOpenNotes}
          onClose={() => { setSelected(null); setAutoOpenNotes(false); }}
          onUpdated={() => { setSelected(null); setAutoOpenNotes(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  const opts = [
    { id: "list", label: "List" },
    { id: "week", label: "Week" },
    { id: "month", label: "Month" },
  ];
  return (
    <div className="inline-flex rounded-full bg-cream border border-sand/80 p-1" data-testid="calendar-view-toggle">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          data-testid={`calendar-view-${o.id}`}
          className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${
            view === o.id ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const WEEKDAYS = [
  { k: "0", name: "Mon" },
  { k: "1", name: "Tue" },
  { k: "2", name: "Wed" },
  { k: "3", name: "Thu" },
  { k: "4", name: "Fri" },
  { k: "5", name: "Sat" },
  { k: "6", name: "Sun" },
];

function fmtHour(h) {
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
}

function WeeklyHoursSummary({ onEdit, refreshKey = 0 }) {
  const [locs, setLocs] = useState(null);
  useEffect(() => {
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => setLocs([]));
  }, [refreshKey]);

  if (locs === null) {
    return <div className="mb-5 rounded-2xl border border-sand/60 bg-cream-soft p-4 text-sm text-ink-soft">Loading availability…</div>;
  }
  if (locs.length === 0) return null;

  return (
    <div className="mb-6 rounded-2xl border border-sand/70 bg-cream-soft" data-testid="weekly-hours-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-sand/60">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Your weekly availability</div>
          <div className="text-xs text-ink-soft mt-0.5">
            If a slot is unavailable on the public site, check here first — outside these hours is closed.
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          data-testid="weekly-hours-edit-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-navy text-cream px-3.5 py-1.5 text-xs font-medium hover:bg-navy-deep transition"
        >
          Edit hours
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.16em] text-ink-soft">
              <th className="text-left px-4 py-2.5 font-medium">Location</th>
              {WEEKDAYS.map((d) => (
                <th key={d.k} className="text-left px-3 py-2.5 font-medium">{d.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {locs.map((l) => (
              <tr key={l.id} data-testid={`weekly-hours-row-${l.slug}`} className="border-t border-sand/40">
                <td className="px-4 py-2.5 text-ink font-medium">{l.name}</td>
                {WEEKDAYS.map((d) => {
                  const hrs = l.schedule?.[d.k];
                  if (!hrs || !Array.isArray(hrs) || hrs.length !== 2) {
                    return (
                      <td key={d.k} className="px-3 py-2.5 text-[12px] text-ink-soft italic" data-testid={`weekly-cell-${l.slug}-${d.k}`}>
                        Closed
                      </td>
                    );
                  }
                  const [start, end] = hrs;
                  return (
                    <td key={d.k} className="px-3 py-2.5 text-[12px] text-ink tabular-nums" data-testid={`weekly-cell-${l.slug}-${d.k}`}>
                      {fmtHour(start)}–{fmtHour(end)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingsList({ bookings, onUpdate, onSelect, onSendReminder }) {
  if (bookings.length === 0) return <Empty text="No bookings yet — they'll appear here as soon as someone books." />;
  return (
    <div className="space-y-3" data-testid="bookings-list">
      {bookings.map((b) => {
        const accentBar = b.color
          ? { backgroundColor: b.color }
          : { backgroundColor: b.kind === "block" ? "#D2C5A9" : "#0E3B4A" };
        if (b.kind === "block") {
          const tinted = !!b.color;
          return (
            <div
              key={b.id}
              data-testid={`block-row-${b.id}`}
              onClick={() => onSelect && onSelect(b)}
              className={`rounded-2xl p-4 md:p-5 cursor-pointer transition-colors border-2 border-dashed ${tinted ? "hover:opacity-95" : "bg-cream border-sand hover:border-navy/50"}`}
              style={tinted ? { backgroundColor: b.color, borderColor: "rgba(255,255,255,0.45)" } : undefined}
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-xs uppercase tracking-[0.18em] ${tinted ? "bg-white/15 text-cream" : "bg-sand/40 text-ink-soft"}`}>Block</div>
                  <div>
                    <div className={`font-serif text-lg ${tinted ? "text-cream" : "text-ink"}`}>{b.custom_label || b.label || "Blocked"}</div>
                    <div className={`text-xs flex items-center gap-3 mt-0.5 ${tinted ? "text-cream/80" : "text-ink-soft"}`}>
                      <span>{format(new Date(b.date), "EEE d MMM")} · {formatTime(b.start_time)}–{formatTime(b.end_time)}</span>
                      <span>·</span>
                      <span>{b.location_name}</span>
                      {b.status === "cancelled" && <span className="text-red-200">(cancelled)</span>}
                    </div>
                  </div>
                </div>
                {b.status !== "cancelled" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onUpdate(b.id, "cancelled"); }}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${tinted ? "bg-white/15 text-cream hover:bg-white/25" : "bg-cream border border-sand hover:border-red-300 hover:text-red-700"}`}
                  >
                    <X size={12} /> Remove block
                  </button>
                )}
              </div>
            </div>
          );
        }
        return (
        <div
          key={b.id}
          data-testid={`booking-row-${b.id}`}
          onClick={() => onSelect && onSelect(b)}
          className="bg-cream-soft border border-sand/70 rounded-2xl p-5 md:p-6 hover:border-navy/50 transition-colors cursor-pointer relative overflow-hidden"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1.5" style={accentBar} />
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between pl-3">
            <div className="flex-grow">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <span className={statusPill(b.status)}>{b.status}</span>
                {b.created_by_admin && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-navy bg-navy/8 px-2 py-0.5 rounded-full">Admin-created</span>
                )}
                {b.buffer_min > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft bg-cream border border-sand px-2 py-0.5 rounded-full">+{b.buffer_min}m buffer</span>
                )}
                {b.custom_label && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-navy bg-cream border border-navy/30 px-2 py-0.5 rounded-full">{b.custom_label}</span>
                )}
                <span className="text-xs text-ink-soft uppercase tracking-[0.16em]">Ref {b.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="font-serif text-xl text-ink mb-1">{b.client_name}</div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-ink-muted">
                <div className="flex items-center gap-2"><Clock size={13} /> {format(new Date(b.date), "EEE d MMM yyyy")} · {formatTime(b.start_time)}</div>
                <div className="flex items-center gap-2"><MapPin size={13} /> {b.location_name}</div>
                <div className="flex items-center gap-2"><Mail size={13} /> <a href={`mailto:${b.client_email}`} onClick={(e) => e.stopPropagation()} className="hover:text-navy">{b.client_email}</a></div>
                <div className="flex items-center gap-2"><Phone size={13} /> {b.client_phone}</div>
              </div>
              <div className="text-xs text-ink-soft mt-2">{b.service_name} · ${b.service_price} · {b.duration_min || 60} min</div>
              {b.notes && (
                <div className="mt-3 p-3 bg-cream rounded-xl text-sm text-ink-muted border border-sand/50">
                  <span className="font-medium text-ink">Notes:</span> {b.notes}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
              {b.status === "confirmed" && b.client_email && (
                <button
                  onClick={() => onSendReminder && onSendReminder(b)}
                  data-testid={`send-reminder-${b.id}`}
                  title={b.reminder_sent_at ? `Last sent ${format(new Date(b.reminder_sent_at), "d MMM, HH:mm")}` : "Send a reminder email to the client now"}
                  className="inline-flex items-center gap-1.5 rounded-full bg-cream text-navy border border-navy/30 hover:bg-navy hover:text-cream px-4 py-2 text-xs font-medium transition-colors"
                >
                  <Send size={12} /> {b.reminder_sent_at ? "Resend reminder" : "Send reminder"}
                </button>
              )}
              {b.status !== "completed" && (
                <button onClick={() => onUpdate(b.id, "completed")} className="inline-flex items-center gap-1.5 rounded-full bg-navy text-cream px-4 py-2 text-xs font-medium hover:bg-navy-deep transition-colors">
                  <Check size={12} /> Mark complete
                </button>
              )}
              {b.status !== "cancelled" && (
                <button onClick={() => onUpdate(b.id, "cancelled")} className="inline-flex items-center gap-1.5 rounded-full bg-cream text-ink border border-sand hover:border-red-300 hover:text-red-700 px-4 py-2 text-xs font-medium transition-colors">
                  <X size={12} /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}

// ---------- Week View ----------
function WeekView({ bookings, onSelect }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((day + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const hours = Array.from({ length: 12 }).map((_, i) => i + 8); // 08:00 → 19:00

  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + delta * 7);
    setWeekStart(d);
  };

  return (
    <div data-testid="week-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-lg text-ink">Week of {format(weekStart, "d MMM yyyy")}</div>
        <div className="flex gap-2">
          <button onClick={() => shift(-1)} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">‹ Prev</button>
          <button onClick={() => setWeekStart(new Date(new Date().setDate(new Date().getDate() - ((new Date().getDay() + 6) % 7))))} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">Today</button>
          <button onClick={() => shift(1)} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">Next ›</button>
        </div>
      </div>
      <div className="bg-cream-soft border border-sand/70 rounded-2xl overflow-auto">
        <div className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] min-w-[800px]">
          <div className="border-b border-r border-sand/60" />
          {days.map((d) => (
            <div key={localDateStr(d)} className="text-center py-2 border-b border-r border-sand/60 last:border-r-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink-soft">{format(d, "EEE")}</div>
              <div className="font-serif text-lg text-ink">{format(d, "d")}</div>
            </div>
          ))}
          {hours.map((h) => (
            <>
              <div key={`h-${h}`} className="text-[10px] text-ink-soft text-right pr-2 pt-1 border-b border-r border-sand/40">{`${h}:00`}</div>
              {days.map((d) => {
                const dStr = localDateStr(d);
                const cellBookings = bookings.filter(
                  (b) => b.date === dStr && parseInt(b.start_time.split(":")[0], 10) === h && b.status !== "cancelled"
                );
                return (
                  <div key={`${dStr}-${h}`} className="border-b border-r border-sand/40 last:border-r-0 min-h-[44px] p-1 relative">
                    {cellBookings.map((b) => {
                      const isBlock = b.kind === "block";
                      const label = b.custom_label || (isBlock ? (b.label || "Blocked") : b.client_name);
                      const style = bookingCellStyle(b);
                      const dur = b.duration_min || 60;
                      // Each hour cell is min 44px tall; render entry as tall as its duration.
                      const rawHeight = Math.max(34, Math.round((dur / 60) * 44) - 4);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => onSelect && onSelect(b)}
                          data-testid={`week-cell-${b.id}`}
                          className={`absolute left-1 right-1 text-left text-[11px] rounded-md px-2 py-1 truncate hover:opacity-90 transition-opacity overflow-hidden ${style.className}`}
                          style={{ ...style.style, top: 4, height: rawHeight, zIndex: 2 }}
                          title={isBlock ? `${label} · ${b.start_time}–${b.end_time} · ${b.location_name}` : `${b.client_name} · ${b.service_name}`}
                        >
                          <div className="leading-tight">
                            {isBlock ? "⛔ " : ""}{label}
                          </div>
                          {dur >= 90 && (
                            <div className="text-[10px] opacity-80 mt-0.5">{b.start_time}–{b.end_time}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Month View ----------
function MonthView({ bookings, onSelect }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const firstDay = new Date(cursor);
  const startDay = (firstDay.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (delta) => {
    const d = new Date(cursor);
    d.setMonth(cursor.getMonth() + delta);
    setCursor(d);
  };
  const todayStr = localDateStr(new Date());

  return (
    <div data-testid="month-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-lg text-ink">{format(cursor, "MMMM yyyy")}</div>
        <div className="flex gap-2">
          <button onClick={() => shift(-1)} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">‹ Prev</button>
          <button onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">Today</button>
          <button onClick={() => shift(1)} className="rounded-full px-4 py-1.5 text-xs bg-cream border border-sand hover:border-navy">Next ›</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 bg-cream-soft border border-sand/70 rounded-2xl p-2">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-[0.22em] text-ink-soft text-center py-2">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} className="min-h-[80px]" />;
          const ds = localDateStr(d);
          const day = bookings.filter((b) => b.date === ds && b.status !== "cancelled");
          const isToday = ds === todayStr;
          return (
            <div
              key={ds}
              data-testid={`month-cell-${ds}`}
              className={`min-h-[80px] rounded-lg p-2 border ${isToday ? "border-navy bg-navy/5" : "border-sand/40 bg-cream"}`}
            >
              <div className={`text-xs font-medium mb-1 ${isToday ? "text-navy" : "text-ink-soft"}`}>{d.getDate()}</div>
              {day.slice(0, 3).map((b) => {
                const isBlock = b.kind === "block";
                const label = b.custom_label || (isBlock ? (b.label || "Blocked") : b.client_name.split(" ")[0]);
                const style = bookingCellStyle(b);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSelect && onSelect(b); }}
                    className={`block w-full text-left text-[10px] truncate rounded px-1.5 py-0.5 mb-0.5 hover:opacity-90 ${style.className}`}
                    style={style.style}
                    title={isBlock ? b.label || "Blocked" : `${b.client_name} · ${b.service_name}`}
                  >
                    {b.start_time} {isBlock ? "⛔" : ""}{label}
                  </button>
                );
              })}
              {day.length > 3 && <div className="text-[10px] text-ink-soft">+{day.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =================== WEBSITE WORKSPACE ===================
function WebsiteWorkspace() {
  const [tab, setTab] = useState("dashboard");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const e = await api.get("/admin/enquiries");
      setEnquiries(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (tab === "enquiries") refresh(); }, [tab]);

  const updateEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const subTabs = [
    { id: "dashboard", label: "Overview", icon: LayoutDashboard },
    { id: "enquiries", label: "Enquiries", icon: Inbox },
    { id: "content", label: "Content", icon: Edit3 },
    { id: "services", label: "Services", icon: Settings },
    { id: "locations", label: "Locations", icon: Building },
    { id: "areas", label: "Areas", icon: Sparkles },
  ];

  return (
    <div data-testid="website-workspace">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Website backend</div>
        <h2 className="font-serif text-3xl text-ink">Manage what visitors see.</h2>
      </div>

      <div className="flex gap-1 mb-8 border-b border-sand/60 overflow-x-auto">
        {subTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`website-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                tab === t.id ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && <DashboardPanel />}
      {tab === "content" && <ContentEditor />}
      {tab === "services" && <ServicesManager />}
      {tab === "locations" && <LocationsManager />}
      {tab === "areas" && <AreasManager />}
      {tab === "enquiries" && (
        loading ? (
          <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>
        ) : (
          <EnquiriesList enquiries={enquiries} onUpdate={updateEnquiryStatus} />
        )
      )}
    </div>
  );
}

// =================== shared bits ===================
function statusPill(status) {
  const map = {
    confirmed: "bg-navy/10 text-navy border-navy/30",
    completed: "bg-navy-light/20 text-navy-deep border-navy/30",
    cancelled: "bg-red-100 text-red-800 border-red-200",
    new: "bg-navy text-cream border-navy",
    read: "bg-cream border-sand text-ink-muted",
    replied: "bg-navy-light/20 text-navy-deep border-navy/30",
    archived: "bg-cream-dark border-sand text-ink-soft",
  };
  return `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${map[status] || "bg-cream border-sand text-ink-muted"}`;
}

// Extracts the bucket + score from a wellbeing-check subject like
// "Wellbeing Check · Generally well (28/40)". Returns null if subject
// isn't a wellbeing submission.
function parseWellbeing(subject) {
  if (!subject || !subject.startsWith("Wellbeing Check")) return null;
  const m = subject.match(/Wellbeing Check\s*[·•|-]?\s*([^()]+?)\s*\((\d+)\/40\)/i);
  if (!m) return { bucket: "Wellbeing", score: null };
  return { bucket: m[1].trim(), score: Number(m[2]) };
}

function EnquiryFilterPill({ id, label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`enquiries-filter-${id}`}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition ${
        active
          ? "bg-navy text-cream"
          : "bg-cream border border-sand text-ink-muted hover:border-navy/40 hover:text-ink"
      }`}
    >
      {label}
      <span className={`tabular-nums text-[11px] px-1.5 rounded-full ${
        active ? "bg-cream/20 text-cream" : "bg-cream-dark text-ink-soft"
      }`}>{count}</span>
    </button>
  );
}

function EnquiriesList({ enquiries, onUpdate }) {
  const [filter, setFilter] = useState("all"); // all | general | wellbeing
  if (enquiries.length === 0) return <Empty text="No enquiries yet." />;

  const tagged = enquiries.map((e) => ({ ...e, _wb: parseWellbeing(e.subject) }));
  const wellbeingCount = tagged.filter((e) => e._wb).length;
  const generalCount = tagged.length - wellbeingCount;
  const visible = tagged.filter((e) => {
    if (filter === "wellbeing") return !!e._wb;
    if (filter === "general") return !e._wb;
    return true;
  });

  return (
    <div data-testid="enquiries-list">
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="enquiries-filter-bar">
        <EnquiryFilterPill id="all" label="All" count={tagged.length} active={filter === "all"} onClick={() => setFilter("all")} />
        <EnquiryFilterPill id="general" label="General" count={generalCount} active={filter === "general"} onClick={() => setFilter("general")} />
        <EnquiryFilterPill id="wellbeing" label="Wellbeing Check" count={wellbeingCount} active={filter === "wellbeing"} onClick={() => setFilter("wellbeing")} />
      </div>

      {visible.length === 0 ? (
        <Empty text="Nothing in this view yet." />
      ) : (
        <div className="space-y-3">
          {visible.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className={`bg-cream-soft border rounded-2xl p-5 md:p-6 ${
              e._wb ? "border-sage/40" : "border-sand/70"
            }`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={statusPill(e.status)}>{e.status}</span>
                    {e._wb && (
                      <span
                        data-testid={`enquiry-wb-tag-${e.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-sage/15 text-sage-deep border-sage/40"
                      >
                        <Sparkles size={11} />
                        Wellbeing{e._wb.score !== null ? ` · ${e._wb.score}/40 · ${e._wb.bucket}` : ""}
                      </span>
                    )}
                    <span className="text-xs text-ink-soft">{format(new Date(e.created_at), "d MMM · h:mm a")}</span>
                  </div>
                  <div className="font-serif text-xl text-ink">{e.name}</div>
                  <div className="text-sm text-ink-muted flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-navy"><Mail size={13} />{e.email}</a>
                    {e.phone && <span className="flex items-center gap-1.5"><Phone size={13} />{e.phone}</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {e.status === "new" && (
                    <button onClick={() => onUpdate(e.id, "read")} className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-4 py-2 text-xs hover:border-navy hover:text-navy transition-colors">Mark read</button>
                  )}
                  {e.status !== "replied" && (
                    <button onClick={() => onUpdate(e.id, "replied")} className="inline-flex items-center gap-1.5 rounded-full bg-navy-light text-cream px-4 py-2 text-xs hover:bg-navy transition-colors"><Check size={12} /> Replied</button>
                  )}
                  {e.status !== "archived" && (
                    <button onClick={() => onUpdate(e.id, "archived")} className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-4 py-2 text-xs hover:bg-cream-dark transition-colors">Archive</button>
                  )}
                </div>
              </div>
              {e.subject && <div className="text-sm font-medium text-ink mb-1">{e.subject}</div>}
              <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-20 text-ink-soft italic font-serif text-lg">{text}</div>;
}

// Use LOCAL date components instead of toISOString() — otherwise on UTC+10/11
// (Tasmania) the calendar shifts every booking by a day, putting Monday's
// sessions into Tuesday's column. Always: YYYY-MM-DD in the user's timezone.
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}


function formatTime(hhmm) {
  if (!hhmm) return "—";
  const [h] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:00 ${suffix}`;
}

function PendingNotesBanner({ onJump }) {
  const [pending, setPending] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const load = () => {
    api.get("/admin/session-notes/pending").then((r) => setPending(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("ams:notes-updated", handler);
    const interval = setInterval(load, 60000);  // refresh every minute
    return () => {
      window.removeEventListener("ams:notes-updated", handler);
      clearInterval(interval);
    };
  }, []);

  if (dismissed || pending.length === 0) return null;
  const next = pending[0];
  const more = pending.length - 1;
  return (
    <div
      data-testid="pending-notes-banner"
      className="mb-6 rounded-2xl border border-amber-300/60 bg-amber-50 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-200/80 text-amber-900">
          <ClipboardList size={16} />
        </span>
        <div>
          <div className="font-serif text-base text-ink">
            {pending.length === 1
              ? "1 session waiting on notes."
              : `${pending.length} sessions waiting on notes.`}
          </div>
          <div className="text-xs text-ink-soft mt-0.5">
            Next: <strong className="text-ink">{next.client_name || next.client_email}</strong> · {next.date} {next.start_time}{more > 0 ? ` (+${more} more)` : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onJump(next)}
          data-testid="open-next-notes"
          className="inline-flex items-center gap-1.5 rounded-full bg-navy text-cream px-4 py-2 text-xs font-medium hover:bg-navy-deep transition-colors"
        >
          Open notes
        </button>
        <button
          onClick={() => setDismissed(true)}
          title="Hide for this session"
          className="text-ink-soft hover:text-ink p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

